import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ChefHeader from '../shared/ui/ChefHeader';
import { Toggle } from '../shared/ui';
import MapView, { ST_KITTS_CENTER } from '../shared/MapView';
import { useAuth } from '../contexts/AuthContext';
import {
  listenToAvailableOrders,
  listenToAcceptedOrders,
  activeDeliveries,
  createDriverOrderReadyNotification,
} from '../shared/services/orderService';
import { setDriverOnline, listenToDriver } from '../shared/services/userService';
import { maybeUpdateOrderETA } from '../shared/eta';
import useRoadRoute from '../shared/useRoadRoute';
import { DriverOrderStatus, MAX_ACTIVE_DELIVERIES, parishesForIsland, orderMatchesDriverZone, orderPickupParish, normalizeIsland } from '../shared/models';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../shared/firebase';
import { useDriverLocation } from '../components/work/useDriverLocation';
import { restaurantCoords, customerCoords, canSeeAvailableOrder } from '../components/work/geo';
import OrderReadyBanner from '../components/work/OrderReadyBanner';
import AvailableOrdersSheet from '../components/work/AvailableOrdersSheet';
import OrderPreviewSheet from '../components/work/OrderPreviewSheet';
import ActiveDeliveriesSheet from '../components/work/ActiveDeliveriesSheet';
import InfoAlert from '../components/work/InfoAlert';
import { Navigation } from 'lucide-react';
import { useWakeLock } from '../../../utils/useWakeLock';

// Statuses where the driver is still heading to the restaurant
// (route target = pickup), vs heading to the customer.
// Whole-island bounds (iOS DriverMapViewWithRoutes.islandBounds) — the idle
// map frames the driver's signup island so the GPS dot stays on screen.
const ISLAND_BOUNDS = {
  'St. Kitts': [[17.24, -62.88], [17.43, -62.53]],
  Nevis: [[17.10, -62.63], [17.21, -62.55]],
};

const PRE_PICKUP = new Set([
  DriverOrderStatus.ACCEPTED,
  DriverOrderStatus.NAVIGATE_TO_RESTAURANT,
  DriverOrderStatus.ARRIVED_AT_RESTAURANT,
]);

// iOS-style floating map button — round icon on top, label underneath.
function MapFab({ emoji, icon = null, label, count, onClick }) {
  return (
    <button
      onClick={onClick}
      className="pointer-events-auto flex w-16 flex-col items-center gap-1 active:scale-95"
    >
      <span className="relative flex h-[50px] w-[50px] items-center justify-center rounded-full bg-white/90 text-2xl shadow-lg backdrop-blur">
        {icon || emoji}
        {count > 0 && (
          <span className="absolute -right-1 -top-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white shadow">
            {count}
          </span>
        )}
      </span>
      <span
        className="line-clamp-2 w-16 px-0.5 py-0.5 text-center text-[10px] font-bold leading-tight text-white"
        style={{
          // Stroked text — dark outline ring instead of a background pill,
          // so the label reads over any basemap (iOS strokedText parity).
          textShadow:
            '-1.2px 0 0 rgba(0,0,0,.9), 1.2px 0 0 rgba(0,0,0,.9), 0 -1.2px 0 rgba(0,0,0,.9), 0 1.2px 0 rgba(0,0,0,.9), -1.2px -1.2px 0 rgba(0,0,0,.9), 1.2px -1.2px 0 rgba(0,0,0,.9), -1.2px 1.2px 0 rgba(0,0,0,.9), 1.2px 1.2px 0 rgba(0,0,0,.9)',
        }}
      >
        {label}
      </span>
    </button>
  );
}

export default function WorkPage() {
  const { user, userProfile, driver, driverId, refreshDriver } = useAuth();
  const navigate = useNavigate();

  // Keep the screen awake while the driver is on the map
  // (iOS DriverMapView sets isIdleTimerDisabled in onAppear).
  useWakeLock();

  // Live driver doc (iOS startListeningToDriverStatus) — keeps the online
  // pill in sync if the status changes on another device.
  const [liveDriver, setLiveDriver] = useState(driver);
  useEffect(() => {
    if (!driverId) return undefined;
    return listenToDriver(driverId, d => setLiveDriver(d));
  }, [driverId]);
  const drv = liveDriver || driver;

  const [rawAvailable, setRawAvailable] = useState([]);
  const [accepted, setAccepted] = useState([]);
  // Online small-vehicle couriers — sub-1-mile orders are reserved for them.
  // Map(driverId -> { vehicleType, seen (ms) }); "active" = seen < 10 min ago.
  const [couriers, setCouriers] = useState(() => new Map());
  const [now, setNow] = useState(() => Date.now());
  // Basemap cycle: dark (driver default) → satellite → standard (iOS 🌍).
  const MAP_MODES = ['standard', 'dark', 'satellite'];
  const [modeIdx, setModeIdx] = useState(0);
  const mapMode = MAP_MODES[modeIdx];
  const nextMode = MAP_MODES[(modeIdx + 1) % MAP_MODES.length];
  const [sheet, setSheet] = useState(null); // 'available' | 'active'
  const [previewOrder, setPreviewOrder] = useState(null);
  const [alert, setAlert] = useState(null); // {title, message}
  const [toggling, setToggling] = useState(false);
  const [dismissedIds, setDismissedIds] = useState(() => new Set());
  const [zoneMenuOpen, setZoneMenuOpen] = useState(false);
  // Zone preference — '' = All Zones (iOS selectedZone, UserDefaults-backed).
  const [zone, setZoneState] = useState(() => localStorage.getItem('driverZone') || '');
  const setZone = (z) => {
    setZoneState(z);
    localStorage.setItem('driverZone', z);
  };
  const zoneRef = useRef(zone);
  zoneRef.current = zone;

  const seenRef = useRef(null); // ids of available orders already alerted
  const audioRef = useRef(null);
  const mapRef = useRef(null);
  const islandFitRef = useRef(false);
  const posFitRef = useRef(false);

  const isOnline = !!drv?.isOnline;
  const isOnlineRef = useRef(isOnline);
  isOnlineRef.current = isOnline;

  const active = useMemo(() => activeDeliveries(accepted), [accepted]);
  const atCapacity = active.length >= MAX_ACTIVE_DELIVERIES;
  const currentOrderId = active[0]?.orderId || null;

  // Browser geolocation → driver pin; Firestore writes only while online.
  const { position } = useDriverLocation(driverId, isOnline, currentOrderId);

  // Latest values for the order listener (registered once) — walking and
  // bicycle couriers only see deliveries inside their vehicle's radius,
  // and motorized drivers yield sub-1-mile orders to active couriers.
  const positionRef = useRef(position);
  positionRef.current = position;
  const vehicleTypeRef = useRef(drv?.vehicleType);
  vehicleTypeRef.current = drv?.vehicleType;
  const couriersRef = useRef(couriers);
  couriersRef.current = couriers;

  // Visible orders: island gate in the listener + vehicle gate here —
  // re-derives whenever position, vehicle type, or courier presence changes.
  // visibleNoZone keeps the pre-zone-filter set so the zone menu can show
  // per-parish counts.
  const visibleNoZone = useMemo(
    () => rawAvailable.filter((o) =>
      canSeeAvailableOrder(o, drv?.vehicleType, position, couriers, now)),
    [rawAvailable, drv?.vehicleType, position, couriers, now]
  );

  const available = useMemo(
    () => visibleNoZone.filter((o) => orderMatchesDriverZone(o, zone)),
    [visibleNoZone, zone]
  );

  // Per-parish order counts for the zone menu.
  const zoneCounts = useMemo(() => {
    const m = new Map();
    visibleNoZone.forEach((o) => {
      const p = orderPickupParish(o);
      if (p) m.set(p, (m.get(p) || 0) + 1);
    });
    return m;
  }, [visibleNoZone]);

  // Watch online drivers for small-vehicle couriers (iOS courierListener).
  useEffect(() => {
    const q = query(collection(db, 'drivers'), where('isOnline', '==', true));
    return onSnapshot(q, (snap) => {
      const m = new Map();
      snap.docs.forEach((d) => {
        const x = d.data();
        m.set(d.id, {
          vehicleType: x.vehicleType,
          seen: x.lastActive?.toMillis?.() ?? x.updatedAt?.toMillis?.() ?? 0,
        });
      });
      setCouriers(m);
    });
  }, []);

  // Courier timestamps age out without firing a snapshot — tick every
  // minute so reserved orders get released to motorized drivers.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);

  // iOS ETAManager parity — keeps the tracked order's estimatedPickupTime /
  // estimatedDeliveryTime fresh as the driver moves (throttled internally).
  useEffect(() => {
    if (isOnline && position && active[0]) {
      maybeUpdateOrderETA(active[0], position, driverId, drv?.vehicleType);
    }
  }, [position, active, isOnline, driverId, drv?.vehicleType]);

  // iOS fitMapToIslandAndDriver — frame the driver's whole signup island
  // plus their GPS dot once (no follow mode, no zoomed-in close-up).
  const driverIsland = normalizeIsland(userProfile?.island || drv?.island) || 'St. Kitts';
  useEffect(() => {
    const map = mapRef.current;
    if (!map || active.length > 0) return; // route/order markers handle framing
    const b = ISLAND_BOUNDS[driverIsland];
    if (position) {
      if (posFitRef.current) return;
      posFitRef.current = true;
      islandFitRef.current = true;
      map.fitBounds([...b, [position.lat, position.lng]], { padding: [24, 24] });
    } else if (!islandFitRef.current) {
      islandFitRef.current = true;
      map.fitBounds(b, { padding: [24, 24] });
    }
  }, [position, driverIsland, active.length]);

  // Live listeners (iOS DriverDashboardViewModel equivalents).
  useEffect(() => {
    if (!driverId) return undefined;
    return listenToAcceptedOrders(driverId, setAccepted);
  }, [driverId]);

  useEffect(() => {
    // iOS island gate — pass the driver's island so St. Kitts drivers never
    // see Nevis orders (and vice versa).
    return listenToAvailableOrders((orders) => {
      const inRange = orders.filter((o) =>
        canSeeAvailableOrder(o, vehicleTypeRef.current, positionRef.current, couriersRef.current) &&
        orderMatchesDriverZone(o, zoneRef.current)
      );
      setRawAvailable(orders);
      if (seenRef.current === null) {
        // First snapshot just seeds the seen-set (no alert on page load).
        seenRef.current = new Set(inRange.map((o) => o.id));
        return;
      }
      const fresh = inRange.filter((o) => !seenRef.current.has(o.id));
      inRange.forEach((o) => seenRef.current.add(o.id));
      // iOS: only the FIRST new order gets the banner + delivery_update
      // notification (newOrders.first in DriverDashboardViewModel).
      if (fresh.length > 0 && isOnlineRef.current && fresh[0]) {
        createDriverOrderReadyNotification(fresh[0], driverId);
      }
    }, undefined, userProfile?.island);
  }, [driverId, userProfile?.island]);

  // iOS: shownOrderIds clears on offline — banners re-arm for orders still
  // available when the driver comes back online.
  const wasOnlineRef = useRef(isOnline);
  useEffect(() => {
    if (!wasOnlineRef.current && isOnline) {
      seenRef.current = new Set();
      setDismissedIds(new Set());
    }
    wasOnlineRef.current = isOnline;
  }, [isOnline]);

  // iOS: header Toggle asks Go Online?/Go Offline? before applying.
  const [pendingOnline, setPendingOnline] = useState(null);

  function requestOnlineChange(next) {
    if (toggling || !driverId || !user || next === isOnline) return;
    // iOS: "Cannot Go Offline" while carrying active deliveries.
    if (!next && active.length > 0) {
      setAlert({
        title: 'Cannot Go Offline',
        message: `You have ${active.length} active ${active.length === 1 ? 'order' : 'orders'}. Please complete all deliveries before going offline.`,
      });
      return;
    }
    setPendingOnline(next);
  }

  async function applyOnline(next) {
    if (!driverId || !user) return;
    setToggling(true);
    try {
      await setDriverOnline(driverId, user.uid, next);
      await refreshDriver();
    } catch (e) {
      setAlert({ title: 'Error', message: e.message || 'Could not update online status.' });
    } finally {
      setToggling(false);
    }
  }

  // ----- Map markers + route -----
  const markers = useMemo(() => {
    const m = [];
    if (position) m.push({ ...position, kind: 'driver', popup: 'You' });
    active.forEach((o) => {
      const r = restaurantCoords(o);
      const c = customerCoords(o);
      if (r) m.push({ ...r, kind: 'restaurant', popup: o.restaurantName || 'Pickup' });
      if (c) m.push({ ...c, kind: 'customer', popup: o.customerName || 'Customer' });
    });
    return m;
  }, [active, position]);

  // Route line: Google-style road-following polyline (OSRM driving
  // geometry). iOS parity — before pickup the driver heads to the
  // restaurant; after pickup the route runs from the driver's live
  // position to the customer. Falls back to the restaurant as origin
  // when the GPS fix hasn't landed, and to a straight segment when
  // routing fails.
  const routeEnds = useMemo(() => {
    const first = active[0];
    if (!first) return null;
    const to = PRE_PICKUP.has(first.status)
      ? restaurantCoords(first)
      : customerCoords(first);
    const from = position || restaurantCoords(first);
    return from && to ? { from, to } : null;
  }, [active, position]);
  const route = useRoadRoute(routeEnds?.from, routeEnds?.to);

  // Banner: first available order not dismissed (hidden at capacity / offline).
  const bannerOrder = useMemo(() => {
    if (!isOnline || atCapacity) return null;
    return available.find((o) => !dismissedIds.has(o.id)) || null;
  }, [available, dismissedIds, isOnline, atCapacity]);

  // iOS DriverOrderReadyBannerView: the driver alert LOOPS while the banner
  // is up (numberOfLoops = -1) and stops on tap/dismiss/offline.
  useEffect(() => {
    if (!bannerOrder) return undefined;
    if (!audioRef.current) audioRef.current = new Audio('/sounds/driver-alert.m4a');
    const audio = audioRef.current;
    audio.loop = true;
    audio.play().catch(() => {});
    return () => {
      audio.pause();
      audio.currentTime = 0;
      audio.loop = false;
    };
  }, [bannerOrder]);

  const driverName =
    drv?.name || userProfile?.name || userProfile?.fullName || 'Driver';

  function handleAccepted(order) {
    setPreviewOrder(null);
    setSheet(null);
    navigate(`/driver/delivery/${order.id}`);
  }

  // iOS header control: status dot + label + switch (restaurant dashboard style).
  const onlinePill = (
    <div className="flex items-center gap-1.5">
      <span
        className={`h-2 w-2 rounded-full ${isOnline ? 'bg-green-500' : 'bg-red-500'}`}
      />
      <span
        className={`text-xs font-semibold ${isOnline ? 'text-green-600' : 'text-red-500'}`}
      >
        {isOnline ? 'Online' : 'Offline'}
      </span>
      <Toggle checked={isOnline} onChange={requestOnlineChange} disabled={toggling} />
    </div>
  );

  // Zone picker — lives in the top status strip (iOS dashboard header).
  // Anchors below the strip via the parent div's `relative`.
  const zoneDropdown = zoneMenuOpen && (
    <>
      <div className="fixed inset-0 z-20" onClick={() => setZoneMenuOpen(false)} />
      <div className="absolute left-0 top-full z-30 mt-2 max-h-72 w-60 overflow-y-auto rounded-2xl bg-white/95 py-1 shadow-xl backdrop-blur">
        <button
          onClick={() => { setZone(''); setZoneMenuOpen(false); }}
          className="flex w-full items-center justify-between px-4 py-2.5 text-left text-sm font-semibold text-gray-800 hover:bg-gray-100"
        >
          <span>All Zones ({visibleNoZone.length})</span>
          {!zone && <span className="text-red-500">✓</span>}
        </button>
        <div className="mx-3 border-t border-gray-200" />
        {parishesForIsland(userProfile?.island).map((p) => (
          <button
            key={p}
            onClick={() => { setZone(p); setZoneMenuOpen(false); }}
            className="flex w-full items-center justify-between px-4 py-2.5 text-left text-sm text-gray-800 hover:bg-gray-100"
          >
            <span>{p} ({zoneCounts.get(p) || 0})</span>
            {zone === p && <span className="text-red-500">✓</span>}
          </button>
        ))}
      </div>
    </>
  );

  const zoneOrderCount = zone ? (zoneCounts.get(zone) || 0) : visibleNoZone.length;

  return (
    // -mb-20 cancels DriverLayout's pb-20 so the map extends under the
    // floating tab bar (iOS parity — the map goes edge-to-edge).
    <div className="flex h-full min-h-0 flex-col -mb-20">
      <ChefHeader title="Dashboard" right={<div className="flex items-center gap-2">{onlinePill}</div>} />

      <div className="relative min-h-0 flex-1">
        <MapView
          center={ST_KITTS_CENTER}
          zoom={13}
          markers={markers}
          route={route}
          mode={mapMode}
          onReady={(m) => {
            mapRef.current = m;
            // Frame the signup island immediately; the GPS-fit effect
            // refits once position arrives.
            if (!islandFitRef.current && active.length === 0) {
              islandFitRef.current = true;
              m.fitBounds(ISLAND_BOUNDS[driverIsland], { padding: [24, 24] });
            }
          }}
        />

        {/* Top: zone picker + status strip, then the new-order banner */}
        <div className="pointer-events-none absolute left-3 right-20 top-3 z-10 space-y-2">
          <div className="pointer-events-auto relative inline-flex items-center gap-2.5 rounded-2xl bg-white/90 px-3 py-2 shadow backdrop-blur">
            <button
              onClick={() => setZoneMenuOpen((v) => !v)}
              className="flex items-center gap-1.5 text-sm font-semibold text-gray-800"
            >
              <span className="text-red-500">📍</span>
              <span className="max-w-[150px] truncate">{zone || 'All Zones'}</span>
              <span className="text-[10px] text-gray-400">▾</span>
            </button>
            <span className="text-xs text-gray-500">
              · {zoneOrderCount} new order{zoneOrderCount === 1 ? '' : 's'}
            </span>
            {active.length > 0 && (
              <span className="text-xs font-semibold text-orange-500">
                {active.length} active
              </span>
            )}
            {zoneDropdown}
          </div>
          {bannerOrder && (
            <OrderReadyBanner
              order={bannerOrder}
              onTap={() => setPreviewOrder(bannerOrder)}
              onDismiss={() =>
                setDismissedIds((s) => new Set(s).add(bannerOrder.id))
              }
            />
          )}
        </div>

        {/* Right edge: all map controls grouped — map mode cycle, locate
            me, then the order lists with live badge counts (iOS parity).
            bottom-24 clears the fixed tab bar now the map runs under it. */}
        <div className="absolute bottom-24 right-0.5 z-10 flex flex-col items-center gap-3">
          <MapFab
            emoji="🌍"
            label={nextMode[0].toUpperCase() + nextMode.slice(1)}
            onClick={() => setModeIdx((i) => (i + 1) % MAP_MODES.length)}
          />
          <MapFab
            emoji="📍"
            icon={<Navigation size={24} className="text-blue-500" />}
            label="Locate Me"
            onClick={() => {
              if (position && mapRef.current) {
                mapRef.current.flyTo([position.lat, position.lng], 15);
              }
            }}
          />
          <MapFab
            emoji="📋"
            label="Available Orders"
            count={available.length}
            onClick={() => setSheet('available')}
          />
          <MapFab
            emoji="🚗"
            label="Active Deliveries"
            count={active.length}
            onClick={() => setSheet('active')}
          />
        </div>

        {/* Offline hint */}
        {!isOnline && (
          <div className="pointer-events-none absolute inset-x-0 bottom-24 z-10 flex justify-center">
            <p className="rounded-full bg-black/60 px-4 py-1.5 text-xs font-semibold text-white">
              You're offline — go online to receive orders
            </p>
          </div>
        )}
      </div>

      <AvailableOrdersSheet
        open={sheet === 'available'}
        onClose={() => setSheet(null)}
        orders={available}
        atCapacity={atCapacity}
        onPreview={(o) => setPreviewOrder(o)}
      />
      <ActiveDeliveriesSheet
        open={sheet === 'active'}
        onClose={() => setSheet(null)}
        deliveries={active}
        onSelect={(o) => {
          setSheet(null);
          navigate(`/driver/delivery/${o.orderId || o.id}`);
        }}
      />
      <OrderPreviewSheet
        order={previewOrder}
        onClose={() => setPreviewOrder(null)}
        atCapacity={atCapacity}
        driverInfo={{ id: driverId, name: driverName }}
        onAccepted={handleAccepted}
      />
      <InfoAlert alert={alert} onClose={() => setAlert(null)} />

      {/* iOS Go Offline?/Go Online? confirmation before flipping the toggle */}
      {pendingOnline !== null && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center px-8">
          <div className="absolute inset-0 bg-black/40" onClick={() => setPendingOnline(null)} />
          <div className="relative w-full max-w-xs rounded-2xl bg-white p-5 text-center shadow-xl">
            <h3 className="text-base font-bold text-gray-900">
              {pendingOnline ? 'Go Online?' : 'Go Offline?'}
            </h3>
            <p className="mt-1.5 text-sm text-gray-500">
              {pendingOnline
                ? "You'll start receiving order offers and your location will be shared while delivering."
                : "You'll stop receiving new order offers until you go back online."}
            </p>
            <div className="mt-4 flex gap-2">
              <button
                onClick={() => setPendingOnline(null)}
                className="flex-1 rounded-xl bg-gray-100 py-2.5 text-sm font-bold text-gray-700 active:scale-[0.98]"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const next = pendingOnline;
                  setPendingOnline(null);
                  applyOnline(next);
                }}
                className={`flex-1 rounded-xl py-2.5 text-sm font-bold text-white active:scale-[0.98] ${
                  pendingOnline ? 'bg-green-500' : 'bg-red-500'
                }`}
              >
                {pendingOnline ? 'Go Online' : 'Go Offline'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
